/* eslint-disable no-restricted-syntax */
import {
	Autocomplete,
	MenuItem,
	CircularProgress,
	IconButton,
	Icon,
	Popper,
	Tooltip,
	InputAdornment
} from "@mui/material";

import {
	DndContext,
	PointerSensor,
	KeyboardSensor,
	closestCenter,
	useSensor,
	useSensors
} from "@dnd-kit/core";
import {
	SortableContext,
	arrayMove,
	sortableKeyboardCoordinates,
	useSortable,
	verticalListSortingStrategy
} from "@dnd-kit/sortable";
import MDBox from "components/Basics/MDBox";
import MDBadge from "components/Basics/MDBadge";
import MDInput from "components/Basics/MDInput";
import MDTypography from "components/Basics/MDTypography";
import lod_ from "lodash";
import { useEffect, useState } from "react";
import axios from "axios";
import { selectCurrentProfile } from "redux-react/reducers/profileReducer";
import { replaceInTextHbr } from "helpers/utilities";
import { handleSpecialCell } from "components/Custom/Tables/DefaultDataTable/functions";
import { queryDictionary } from "helpers/dictionary";
import { useDispatch, useSelector } from "react-redux";
import { display } from "redux-react/reducers/snackBarReducer";
import { t } from "i18next";
import { openFormV2Dialog } from "redux-react/reducers/applicationReducer";
import { getResource } from "helpers/resource";

const AdaptivePopper = ({ style, ...props }) => (
	<Popper
		{...props}
		style={{ ...style, width: "auto", minWidth: style?.width, maxWidth: "60vw" }}
	/>
);

const LOCATIONS = {
	assistant: {
		label: "Assistant",
		color: "info"
	},
	centralAssistant: {
		label: "Central Assistant",
		color: "success"
	},
	account: {
		label: "Account",
		color: "warning"
	},
	catalogAssistant: {
		label: "Catalog Assistant",
		color: "error"
	},
	allCatalogs: {
		label: "All Catalogs",
		color: "primary"
	}
};

const getSortableTransform = transform => {
	if (lod_.isNil(transform)) {
		return undefined;
	}

	const { x = 0, y = 0, scaleX = 1, scaleY = 1 } = transform;
	return `translate3d(${x}px, ${y}px, 0) scaleX(${scaleX}) scaleY(${scaleY})`;
};

const SortableTagItem = ({
	id,
	option,
	disabled,
	draggable = true,
	canUseFormV2,
	onEdit,
	onDelete,
	renderOptionContent
}) => {
	const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({
		id
	});

	return (
		<MDBox
			ref={setNodeRef}
			style={{
				transform: getSortableTransform(transform),
				transition,
				opacity: isDragging ? 0.7 : 1,
				width: "100%"
			}}
			bgColor="light"
			p={0.5}
			m={0.2}
			borderRadius="md"
			display="flex"
			flexDirection="row"
			alignItems="center"
			justifyContent="space-between"
		>
			{draggable && (
				<IconButton size="small" disabled={disabled} title="Move" {...attributes} {...listeners}>
					<Icon fontSize="small">drag_indicator</Icon>
				</IconButton>
			)}
			<MDBox display="flex" alignItems="flex-start" flexGrow={1} minWidth={0}>
				{renderOptionContent(option)}
			</MDBox>
			<MDBox display="flex" alignItems="center" ml={1}>
				{canUseFormV2 && (
					<IconButton size="small" onClick={onEdit} disabled={disabled} title={t("SETTINGS.edit")}>
						<Icon fontSize="small">edit</Icon>
					</IconButton>
				)}
				<IconButton size="small" onClick={onDelete} disabled={disabled}>
					<Icon>close</Icon>
				</IconButton>
			</MDBox>
		</MDBox>
	);
};

/**
 * Whitelist dynamic input component V2
 * This component allows users to select items from a dynamic whitelist based on API actions.
 * It supports both single and multiple selections, loading suggestions from a specified API,
 * and rendering options in a customizable way.
 * @param {Object} props - The component props
 * @param {string} props.label - The label for the input field
 * @param {boolean} props.disabled - Whether the input is disabled
 * @param {string|Array} props.value - The current value(s) of the input
 * @param {Function} props.onChange - Callback function to handle value changes
 * @param {Object} props.item - The item configuration containing whitelistDynamic and other properties
 * @param {Array} props.listOfApis - List of available APIs to fetch data from
 * @param {string} props.error - Error message to display if any
 * @param {Object} props.InputProps - Additional props for the input component
 * @param {boolean} props.isArray - Whether the input is expected to handle an array
 * @param {"list"|"number"} props.displaySelection - How to render selected values in array mode -> as a list of items or as a number of selected items
 * @param {boolean} props.displaySource - Whether to display the source location badge for each option
 * @param {boolean} props.draggable - Whether the selected items are draggable (only in array mode)
 * @param {...Object} rest - Additional props to pass to the Autocomplete component
 * @returns {JSX.Element} - Rendered Autocomplete component with dynamic suggestions
 */
const WhitelistItemV2 = ({
	label,
	disabled,
	value,
	onChange,
	item,
	listOfApis,
	error,
	InputProps,
	isArray = false,
	displaySelection = "list",
	displaySource = true,
	draggable = true,
	...rest
}) => {
	const dispatch = useDispatch();
	const assistantID = useSelector(state => selectCurrentProfile(state).assistantID);
	const { user } = useSelector(state => state);

	const [dictionary, setDictionary] = useState({});
	const [resource, setResource] = useState({});

	const [options, setOptions] = useState([]);
	const [loading, setLoading] = useState(false);
	const [hasMore, setHasMore] = useState(true);
	const [currentPage, setCurrentPage] = useState(1);
	const [currentSearchText, setCurrentSearchText] = useState("");

	const [currentValue, setCurrentValue] = useState(null);

	let whitelistDynamic = item.whitelistDynamic || {};
	const attribute = whitelistDynamic?.params?.attribute || "code";
	const saveObject = whitelistDynamic?.params?.saveObject === true;
	const objectFields = lod_.isArray(whitelistDynamic?.params?.objectFields)
		? whitelistDynamic?.params?.objectFields
		: [];
	const fieldsToSave = lod_
		.uniq([attribute, ...objectFields])
		.filter(field => !lod_.isNil(field) && field !== "");

	const getAttributeFromStoredValue = option => {
		if (lod_.isObject(option)) {
			return lod_.get(option, attribute, lod_.get(option, "code", ""));
		}
		return option;
	};

	const buildObjectValue = option => {
		if (!lod_.isObject(option)) {
			return option;
		}

		const objectValue = {};
		for (let field of fieldsToSave) {
			objectValue[field] = lod_.get(option, field, null);
		}

		return objectValue;
	};

	const mapValueToSave = option => {
		if (saveObject) {
			return buildObjectValue(option);
		}

		return lod_.get(option, attribute, option);
	};

	function displayError(
		error = t("FORMS.ERRORS.cannotLoadAPIDatas", {
			label
		})
	) {
		dispatch(
			display({
				message: error,
				type: "error"
			})
		);
	}

	/**
	 * Get correct API and action
	 * @returns {Object} - Object containing the API and action if found, otherwise an empty object
	 */
	function getCorrectAction() {
		const apiName = whitelistDynamic?.apiName;
		const actionName = whitelistDynamic?.action;
		if (lod_.isNil(apiName) || lod_.isNil(actionName)) {
			return {};
		}
		if (lod_.isNil(listOfApis)) {
			return {};
		}
		const api = listOfApis.find(item => item.name === apiName);
		if (lod_.isNil(api) || lod_.isEmpty(api)) {
			return {};
		}
		const action = lod_.get(api, `config.action.${actionName}`, null);
		if (lod_.isNil(action) || lod_.isEmpty(action)) {
			return {};
		}
		return { api, action };
	}

	/**
	 * Get API action details
	 * @returns {Object} - Object containing success status, params, API, and action if found, otherwise an object with success: false
	 */
	function getApiAction() {
		try {
			const apiName = whitelistDynamic?.apiName;
			const actionName = whitelistDynamic?.action;
			const params = whitelistDynamic?.params || {};

			if (
				lod_.isNil(params) ||
				lod_.isEmpty(params) ||
				lod_.isNil(actionName) ||
				lod_.isNil(apiName)
			) {
				return { success: false };
			}

			if (lod_.isNil(listOfApis)) {
				return { success: false };
			}

			const API = listOfApis.find(item => item.name === apiName);

			if (lod_.isNil(API) || lod_.isEmpty(API)) {
				return { success: false };
			}

			const action = lod_.get(API, `config.action.${actionName}`, null);
			if (lod_.isNil(action) || lod_.isEmpty(action)) {
				return { success: false };
			}

			return {
				success: true,
				params,
				API,
				action
			};
		} catch {
			return { success: false };
		}
	}

	/**
	 * Get params from the action and additional parameters
	 * @param {Object} action - The action object containing API details
	 * @param {Object} params - Additional parameters to override action defaults
	 * @returns {Object} - An object containing collection, mergeCollections, account, and
	 */
	const getParams = (action, params) => {
		// Collection can be overridden by params.collection
		const collection = params.collection ?? resource?.storage?.collection ?? action.collection;

		// Merge collections is an array of collections name, that will be requested in the same time
		// The result will be merged in the same array
		const mergeCollections = action.mergeCollections || [];

		const account = action.account ?? false;

		const context = rest?.context || {};
		const mapping = params?.mapping || {};
		const headersMapping = mapping?.headers || "{}";

		const poolData = {
			context // Current config
		};

		const headersMappingObject = replaceInTextHbr(headersMapping, poolData, true);

		return {
			collection,
			mergeCollections,
			account,
			headersMappingObject
		};
	};

	/**
	 * Load suggestions with customizable display
	 * @param {string} searchText - The text to search for suggestions
	 * @param {number} limit - The maximum number of suggestions to return
	 * @param {number} page - The page number for pagination
	 * @returns {Promise<Object>} - An object containing results and a boolean indicating if more
	 */
	async function loadSuggestions(searchText, limit = 10, page = 1) {
		try {
			const { success, params, API, action } = getApiAction();
			if (!success) {
				displayError();
				return { results: [] };
			}

			const { searchFields, filters = {}, locations = ["assistant"] } = params;

			const query = {
				$or: []
			};

			for (let field of searchFields) {
				query.$or.push({
					[field]: { $regex: searchText, $options: "i" }
				});
			}

			const sort = searchFields.length > 0 ? { [searchFields[0]]: 1 } : {};

			const { collection, mergeCollections, account, headersMappingObject } = getParams(
				action,
				params
			);

			const { host } = API.config;

			let config = {
				method: action.method ?? "GET",
				maxBodyLength: Infinity,
				url: `${host}/api/v1/ressource/${collection}/items?search=${searchText}&page=${page}&limit=${limit}&account=${account}`,
				headers: {
					Authorization: `Bearer ${user.token}`,
					assistantID,
					"Content-Type": "application/json",
					...headersMappingObject
				}
			};

			if (config.method === "GET") {
				config.data = JSON.stringify({ query });
			} else {
				config.data = {
					filter: lod_.merge({}, action.filter, filters),
					searchFields,
					mergeCollections,
					attribute: whitelistDynamic?.params?.attribute ?? "code", // Optional
					page,
					limit,
					query,
					locations,
					sort
				};
			}

			const result = await new Promise((resolve, reject) => {
				axios
					.request(config)
					.then(response => {
						resolve(response.data);
					})
					.catch(error => {
						reject(error);
					});
			});

			const results = result?.results || [];
			const currentPage = result?.currentPage || 1;
			const totalPages = result?.totalPages || 1;

			const hasMore = results.length === limit && currentPage < totalPages; // If we got less than the limit, no more data

			return { results, hasMore };
		} catch (error) {
			displayError();
			return { results: [], hasMore: false };
		}
	}

	/**
	 * When a value is seted, load the selected values based on the current value
	 * We must use API / action to get the correct values
	 * @param {object} query - The query object to filter the selected values
	 * @returns {Promise<Object>} - A promise that resolves to an object containing the results
	 */
	async function loadSelectedValues(query) {
		try {
			const { success, params, API, action } = getApiAction();
			if (!success) {
				displayError();
				return { results: [] };
			}

			const { collection, mergeCollections, account, headersMappingObject } = getParams(
				action,
				params
			);

			const { filters = {}, locations = ["assistant"] } = params;

			const { host } = API.config;

			let config = {
				method: "POST",
				maxBodyLength: Infinity,
				url: `${host}/api/v1/ressource/${collection}/items?page=1&limit=0&account=${account}`,
				headers: {
					Authorization: `Bearer ${user.token}`,
					assistantID,
					"Content-Type": "application/json",
					...headersMappingObject
				}
			};

			config.data = {
				filter: lod_.merge({}, action.filter, filters),
				mergeCollections,
				attribute: whitelistDynamic?.params?.attribute ?? "code", // Optional
				page: 1,
				limit: null,
				query,
				locations
			};

			const result = await new Promise((resolve, reject) => {
				axios
					.request(config)
					.then(response => {
						resolve(response.data);
					})
					.catch(error => {
						reject(error);
					});
			});

			const results = result?.results || [];

			return { results };
		} catch (error) {
			displayError();
			return { results: [] };
		}
	}

	/**
	 * Search for suggestions based on the input value
	 * @param {string} val - The search text
	 * @param {number} page - The page number for pagination
	 * @param {boolean} append - Whether to append results to the existing options
	 * @returns {Promise<void>} - A promise that resolves when the search is complete
	 */
	const search = async (val, page = 1, append = false) => {
		setLoading(true);
		try {
			const { results, hasMore: moreAvailable } = await loadSuggestions(val, 10, page);

			if (append) {
				setOptions(prevOptions => [...prevOptions, ...results]);
			} else {
				setOptions(results);
				setCurrentPage(1);
				setCurrentSearchText(val);
			}

			setHasMore(moreAvailable);
			setCurrentPage(page);
		} catch (error) {
			displayError();
		} finally {
			setLoading(false);
		}
	};

	/**
	 * Debounced search function to avoid excessive API calls
	 */
	const debouncedSearch = lod_.debounce(val => {
		search(val);
	}, 300);

	/**
	 * Load more results when the user scrolls to the bottom of the listbox
	 */
	const loadMore = async () => {
		if (hasMore && !loading) {
			const nextPage = currentPage + 1;
			await search(currentSearchText, nextPage, true);
		}
	};

	/**
	 * From a given option, render the label (must return a string for Autocomplete)
	 * @param {Object} option - The option to render
	 * @returns {string} - The rendered label for the option as a string
	 */
	const getOptionLabel = option => {
		// Display mode can be "text" / "grid"
		const displayMode = whitelistDynamic.params.displayMode || "text";
		// Display, when it's "text" is: " {{{ json code }}} {{{ json name }}}", and when it's "grid" is: ["code", "name"]
		const display = whitelistDynamic.params.display || "code";

		if (displayMode === "grid") {
			// For grid mode, return a simple string representation
			const displayFields = Array.isArray(display) ? display : [display];
			return displayFields.map(field => lod_.get(option, field, "")).join(" ");
		} else {
			// For text mode, use the template replacement
			const replacedText = replaceInTextHbr(display, option);
			return replacedText;
		}
	};

	const getOptionKey = option => {
		return lod_.get(option, attribute, lod_.get(option, "code", ""));
	};

	/**
	 * From a given option, render the full visual representation (can return JSX)
	 * @param {Object} option - The option to render
	 * @returns {JSX.Element|string} - The rendered visual for the option
	 */
	const renderOptionContent = option => {
		const optionObject = !lod_.isNil(option) && typeof option === "object" ? option : {};
		const locationCode = lod_.get(
			optionObject,
			"faibrik_resource_location",
			lod_.get(resource, "faibrik_resource_location", "assistant")
		);
		const location = LOCATIONS[locationCode] || LOCATIONS.assistant;

		const renderWithLocationBadge = content => {
			const isPrimitiveContent = typeof content === "string" || typeof content === "number";

			return (
				<MDBox
					display="flex"
					flexDirection="column"
					alignItems="flex-start"
					width="100%"
					overflow="hidden"
				>
					{isPrimitiveContent ? (
						<Tooltip title={content} placement="left" enterDelay={600} disableInteractive>
							<MDTypography
								variant="button"
								fontWeight="regular"
								noWrap
								sx={{ maxWidth: "100%", display: "block" }}
							>
								{content}
							</MDTypography>
						</Tooltip>
					) : (
						<MDBox overflow="hidden" width="100%">
							{content}
						</MDBox>
					)}
					{displaySource && (
						<MDBox mt={0.25}>
							<MDBadge
								badgeContent={location.label}
								color={location.color}
								variant="contained"
								size="xs"
							/>
						</MDBox>
					)}
				</MDBox>
			);
		};

		/**
		 * If we have a simple value (string, number), just return it
		 */
		if (lod_.isNil(option) || typeof option !== "object") {
			return renderWithLocationBadge(option);
		}

		/**
		 * Otherwise, render based on display mode
		 */
		const { action = {} } = getCorrectAction();
		// Display mode can be "text" / "grid"
		const displayMode = whitelistDynamic.params.displayMode || "text";
		// Display, when it's "text" is: " {{{ json code }}} {{{ json name }}}", and when it's "grid" is: ["code", "name"]
		const display = whitelistDynamic.params.display || "code";

		switch (displayMode) {
			case "grid": {
				const displayFields = Array.isArray(display) ? display : [display];

				return renderWithLocationBadge(
					<MDBox display="flex" flexDirection="row" alignItems="center">
						{displayFields.map((field, index) => {
							let fieldValue = lod_.get(option, field);

							const cellElement = {
								value: fieldValue,
								column: {
									id: field
								},
								row: {
									original: option,
									values: option
								},
								render: () => {
									return fieldValue;
								}
							};

							return (
								<MDBox>
									<MDBox key={index} m={0.5}>
										{handleSpecialCell(
											cellElement,
											field,
											{ [resource?.format?.dictionary ?? action.collection]: dictionary },
											{ collectionDefault: action?.collection },
											null,
											action?.collection
										)}
									</MDBox>
								</MDBox>
							);
						})}
					</MDBox>
				);
			}
			case "text":
			default: {
				const replacedText = replaceInTextHbr(display, option);
				return renderWithLocationBadge(replacedText);
			}
		}
	};

	/**
	 * From a given code, render the value
	 * @param {string} option - The code to render
	 */
	const renderValue = async option => {
		if (lod_.isNil(option) || lod_.isEmpty(option)) {
			return;
		}

		const { action } = getCorrectAction();
		if (
			!lod_.isNil(action) &&
			!lod_.isEmpty(action) &&
			(resource?.storage?.collection ?? action.collection)
		) {
			const attributeQuery = isArray
				? lod_.compact(
						(lod_.isArray(option) ? option : [option]).map(val => getAttributeFromStoredValue(val))
					)
				: getAttributeFromStoredValue(option);

			if (isArray && attributeQuery.length === 0) {
				setCurrentValue(lod_.isArray(option) ? option : []);
				return;
			}

			if (!isArray && (lod_.isNil(attributeQuery) || attributeQuery === "")) {
				setCurrentValue(option);
				return;
			}

			const result = await loadSelectedValues({
				[attribute]: isArray ? { $in: attributeQuery } : attributeQuery,
				active: { $in: [true, false, null] }
			});

			const objects = result?.results || [];

			if (!lod_.isEmpty(objects)) {
				if (isArray) {
					const originalOrderValues = Array.isArray(option) ? option : [option];
					const sortedObjects = originalOrderValues.map(currentOption => {
						const matchedObject = objects.find(
							item => lod_.get(item, attribute, item) === currentOption
						);

						return lod_.isNil(matchedObject) ? currentOption : matchedObject;
					});

					setCurrentValue(sortedObjects);
				} else {
					setCurrentValue(objects[0]);
				}
			} else {
				setCurrentValue(option);
			}
		}
	};

	/**
	 * Handle changes in the Autocomplete component
	 * @param {Object} event - The event object from the Autocomplete
	 * @param {Object} newValue - The new value selected by the user
	 * @param {string} reason - The reason for the change (e.g., "selectOption", "clear")
	 * @returns {void} - Handles the change event for the Autocomplete component
	 */
	const onChangeAutocomplete = (event, newValue, reason) => {
		if (reason === "selectOption" || reason === "removeOption") {
			if (isArray) {
				// If isArray, we expect newValue to be an array of selected options
				const valueToSave = newValue.map(item => mapValueToSave(item));
				onChange(valueToSave);
			} else {
				const valueToSave = mapValueToSave(newValue);
				onChange(valueToSave);
			}
		} else if (reason === "clear" && event?.type === "click") {
			// Only clear when user explicitly clicks the clear button
			onChange(isArray ? [] : "");
		}
	};

	/**
	 * From a given code, get the dictionary
	 * @param {string} code - The code to query the dictionary
	 * @returns {Promise<void>} - A promise that resolves when the dictionary is fetched
	 */
	const getDictionary = async code => {
		const dictionaryResult = await queryDictionary(dispatch, code);
		setDictionary(dictionaryResult);

		const resource = await getResource(dispatch, code);
		setResource(resource);
	};

	useEffect(() => {
		if (lod_.isNil(value) || lod_.isEmpty(value)) {
			setCurrentValue(null);
			return;
		}
		renderValue(value);
	}, [value, listOfApis]);

	useEffect(() => {
		const { action } = getCorrectAction();
		if (
			!lod_.isNil(action) &&
			!lod_.isEmpty(action) &&
			(resource?.storage?.collection ?? action.collection)
		) {
			getDictionary(resource?.storage?.collection ?? action.collection);
			search("");
		}
	}, [whitelistDynamic, item, listOfApis]);

	// Cleanup debounced function on unmount
	useEffect(() => {
		return () => {
			debouncedSearch.cancel();
		};
	}, [debouncedSearch]);

	// Handle scroll detection for infinite scroll
	const handleListboxScroll = event => {
		const listboxNode = event.currentTarget;
		const isNearBottom =
			listboxNode.scrollTop + listboxNode.clientHeight >= listboxNode.scrollHeight - 10;

		if (isNearBottom && hasMore && !loading) {
			loadMore();
		}
	};

	// Create options with loading indicator if needed
	const optionsWithLoading = [...options];
	if (loading && hasMore && options.length > 0) {
		optionsWithLoading.push({ isLoading: true, code: "loading-indicator" });
	}

	/**
	 * Get the value to be used in the Autocomplete component
	 * @returns {string|Array|null} - Returns the current value as an array if isArray
	 */
	const getValue = () => {
		if (isArray) {
			return currentValue || [];
		} else {
			return currentValue || null;
		}
	};

	const canUseFormV2 = !lod_.isEmpty(dictionary) && resource?.active === true;
	const sensors = useSensors(
		useSensor(PointerSensor, {
			activationConstraint: {
				distance: 8
			}
		}),
		useSensor(KeyboardSensor, {
			coordinateGetter: sortableKeyboardCoordinates
		})
	);

	const getTagItemId = (option, index) => {
		const attribute = whitelistDynamic?.params?.attribute || "code";
		const rawId = lod_.get(option, "_id", lod_.get(option, attribute, getOptionKey(option)));
		return `${rawId}-${index}`;
	};

	const openCreateFormV2Dialog = () => {
		const { action } = getCorrectAction();
		if (lod_.isNil(action) || lod_.isEmpty(action)) {
			return;
		}

		dispatch(
			openFormV2Dialog({
				dictionary,
				item: {},
				edit: false,
				target: resource?.storage?.collection ?? action.collection,
				save: {
					onSuccess: (res, updatedItem) => {
						search("");

						const attribute = whitelistDynamic?.params?.attribute || "code";
						if (isArray === true) {
							// Add the new item to the array
							const selectedValue = lod_.get(updatedItem, attribute, "");
							if (!lod_.isEmpty(selectedValue)) {
								const newArray = [...(currentValue || []), updatedItem];
								onChange(newArray.map(item => lod_.get(item, attribute, item)));
							}
						} else {
							const selectedValue = lod_.get(updatedItem, attribute, "");
							if (!lod_.isEmpty(selectedValue)) {
								onChange(selectedValue);
							}
						}
					}
				}
			})
		);
	};

	const openEditFormV2Dialog = (itemToEdit = null) => {
		const { action } = getCorrectAction();
		if (lod_.isNil(action) || lod_.isEmpty(action)) {
			return;
		}

		// For array mode, use the itemToEdit parameter; otherwise use currentValue
		const itemToUpdate = isArray ? itemToEdit : currentValue;

		if (
			lod_.isNil(itemToUpdate) ||
			lod_.isEmpty(itemToUpdate) ||
			lod_.isNil(lod_.get(itemToUpdate, "_id"))
		) {
			return;
		}

		dispatch(
			openFormV2Dialog({
				dictionary,
				item: itemToUpdate,
				edit: true,
				target: resource?.storage?.collection ?? action.collection,
				save: {
					onSuccess: (res, updatedItem) => {
						search("");
						if (isArray) {
							// Update the specific item in the array
							const attribute = whitelistDynamic?.params?.attribute || "code";
							const updatedArray = currentValue.map(el => {
								if (lod_.get(el, "_id") === lod_.get(updatedItem, "_id")) {
									return updatedItem;
								}
								return el;
							});
							onChange(updatedArray.map(item => lod_.get(item, attribute, item)));
						} else {
							renderValue(value);
						}
					}
				}
			})
		);
	};

	return (
		<Autocomplete
			id="autocomplete-whitelist-item-v2"
			style={{
				backgroundColor: "white"
			}}
			disabled={disabled}
			disableCloseOnSelect={isArray}
			options={optionsWithLoading}
			filterOptions={options => options}
			onInputChange={(event, inputValue, reason) => {
				if (reason === "input") {
					debouncedSearch(inputValue);
				}
			}}
			onOpen={() => {
				// if no value in input, reset search
				if (lod_.isNil(currentSearchText) || lod_.isEmpty(currentSearchText)) {
					search("");
					setCurrentSearchText("");
					setCurrentPage(1);
					setHasMore(true);
					setOptions([]);
				}
			}}
			onBlur={() => {
				// Reset search when input is blurred
				setCurrentSearchText("");
			}}
			onChange={onChangeAutocomplete}
			isOptionEqualToValue={(option, selectedValue) => {
				if (option?.isLoading === true || selectedValue?.isLoading === true) {
					return false;
				}

				return getOptionKey(option) === getOptionKey(selectedValue);
			}}
			// Option label rendering
			getOptionLabel={option => {
				if (option.isLoading) return "";
				return getOptionLabel(option);
			}}
			multiple={isArray} // Allow multiple selection if isArray is true
			loading={loading && options.length === 0} // Only show loading on input when no options yet
			// Set the value based on currentValue state
			value={getValue()}
			// Disable the input if disabled prop is true
			renderValue={(option, getItemProps) => {
				if (lod_.isNil(option) || lod_.isEmpty(option)) {
					return null;
				}
				return renderOptionContent(option);
			}}
			// Additional props for the Autocomplete component
			slots={{ popper: AdaptivePopper }}
			slotProps={{
				listbox: {
					onScroll: handleListboxScroll,
					style: { maxHeight: "35vh" }
				}
			}}
			// Render options with loading indicator
			renderOption={(props, option, state) => {
				if (option.isLoading) {
					return (
						<MenuItem {...props} key="loading-indicator" disabled>
							<MDBox display="flex" justifyContent="center" alignItems="center" width="100%" p={1}>
								<CircularProgress size={20} />
							</MDBox>
						</MenuItem>
					);
				}

				const optionKey = getOptionKey(option);
				const optionAttributeValue = lod_.get(option, attribute, "");
				const selectedValues = lod_.isArray(currentValue)
					? currentValue.map(val => getAttributeFromStoredValue(val))
					: [];
				return (
					<MenuItem
						{...props}
						key={`${optionKey}-${state.index}`}
						selected={(isArray && selectedValues.includes(optionAttributeValue)) || false}
						disabled={
							!isArray && lod_.isEqual(getAttributeFromStoredValue(value), optionAttributeValue)
						}
					>
						{renderOptionContent(option)}
					</MenuItem>
				);
			}}
			// Render tags when multiple selection is enabled
			renderTags={selectedValues => {
				if (isArray) {
					if (displaySelection === "number") {
						return (
							<MDBox display="flex" alignItems="center" mr={0.5}>
								<MDBadge
									badgeContent={selectedValues.length}
									color="info"
									variant="contained"
									size="lg"
									circular
									sx={{
										"& .MuiBadge-badge": {
											fontSize: "0.8rem",
											minWidth: 24,
											height: 24,
											padding: "0 6px"
										}
									}}
								/>
							</MDBox>
						);
					}

					const sortableItems = selectedValues.map((option, index) => ({
						id: getTagItemId(option, index),
						option
					}));

					const onDragEnd = event => {
						const { active, over } = event;
						if (lod_.isNil(over) || active.id === over.id) {
							return;
						}

						const oldIndex = sortableItems.findIndex(item => item.id === active.id);
						const newIndex = sortableItems.findIndex(item => item.id === over.id);

						if (oldIndex < 0 || newIndex < 0) {
							return;
						}

						const reorderedValues = arrayMove(sortableItems, oldIndex, newIndex).map(
							item => item.option
						);

						setCurrentValue(reorderedValues);
						onChangeAutocomplete({}, reorderedValues, "selectOption");
					};

					return (
						<DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={onDragEnd}>
							<SortableContext
								items={sortableItems.map(item => item.id)}
								strategy={verticalListSortingStrategy}
							>
								<MDBox display="flex" flexDirection="column" width="100%" mt={0.5}>
									{sortableItems.map(({ id, option }) => {
										const onDelete = () => {
											const attribute = whitelistDynamic?.params?.attribute || "code";
											const newValue = selectedValues.filter(
												el => lod_.get(el, attribute) !== lod_.get(option, attribute)
											);
											onChangeAutocomplete({}, newValue, "selectOption");
										};

										const onEdit = () => {
											openEditFormV2Dialog(option);
										};

										return (
											<SortableTagItem
												key={id}
												id={id}
												option={option}
												draggable={draggable}
												disabled={disabled}
												canUseFormV2={canUseFormV2}
												onEdit={onEdit}
												onDelete={onDelete}
												renderOptionContent={renderOptionContent}
											/>
										);
									})}
								</MDBox>
							</SortableContext>
						</DndContext>
					);
				}
				return null; // No tags to render if not an array
			}}
			// Render input with debounced search
			renderInput={params => (
				<MDInput
					{...params}
					label={label}
					InputProps={{
						placeholder: t("FORMS.PLACEHOLDERS.search"),
						...params.InputProps,
						endAdornment: (
							<>
								{canUseFormV2 && (
									<InputAdornment position="end" style={{ marginRight: 2 }}>
										<Tooltip title={t("SETTINGS.add")} placement="top">
											<IconButton size="small" onClick={openCreateFormV2Dialog} disabled={disabled}>
												<Icon fontSize="small">add</Icon>
											</IconButton>
										</Tooltip>
										{isArray === false && (
											<Tooltip title={t("SETTINGS.edit")} placement="top">
												<span>
													<IconButton
														size="small"
														onClick={openEditFormV2Dialog}
														disabled={
															disabled ||
															lod_.isNil(currentValue) ||
															lod_.isNil(lod_.get(currentValue, "_id"))
														}
													>
														<Icon fontSize="small">edit</Icon>
													</IconButton>
												</span>
											</Tooltip>
										)}
									</InputAdornment>
								)}
								{params.InputProps.endAdornment}
							</>
						)
					}}
				/>
			)}
		/>
	);
};
export default WhitelistItemV2;
